import { Stack, Typography, useTheme, Box, Checkbox, FormControlLabel } from "@mui/material";
import { FoodsTextInput } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { JsonAttributeType } from "src/api/client-api/jsonAttributes/useJsonAttribute";
import { ProductFormType } from "../../../ProductForm.types";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

type JsonAttributesFieldProps = {
    product: ProductFormType;
    setProduct: (updater: (prev: ProductFormType) => ProductFormType) => void;
};

export const JsonAttributesField = ({ product, setProduct }: JsonAttributesFieldProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { entitiesJsonAttributes } = useFoodpilotContext();

    const productAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.product] || [];

    if (productAttributes.length === 0) {
        return null;
    }

    const handleAttributeChange = (identifier: string, value: any) => {
        setProduct((prevProduct) => ({
            ...prevProduct,
            jsonAttributes: {
                ...prevProduct.jsonAttributes,
                [identifier]: value,
            },
        }));
    };

    const renderAttributeField = (attribute: any) => {
        const currentValue = product.jsonAttributes?.[attribute.identifier];

        switch (attribute.type) {
            case JsonAttributeType.STRING:
                return (
                    <Stack key={attribute.identifier} flexDirection="column" gap={theme.spacing(1)}>
                        <Typography variant="body-semibold">{attribute.label}</Typography>
                        <FoodsTextInput
                            value={currentValue || ""}
                            onChange={(value: string) => {
                                handleAttributeChange(attribute.identifier, value);
                            }}
                        />
                    </Stack>
                );

            case JsonAttributeType.INTEGER:
            case JsonAttributeType.FLOAT:
                return (
                    <Stack key={attribute.identifier} flexDirection="column" gap={theme.spacing(1)}>
                        <Typography variant="body-semibold">{attribute.label}</Typography>
                        <FoodsTextInput
                            value={currentValue?.toString() || ""}
                            onChange={(value: string) => {
                                // Handle empty value
                                if (value.trim() === "") {
                                    handleAttributeChange(attribute.identifier, null);
                                    return;
                                }

                                // For numeric fields, try to convert to number
                                const numValue = parseFloat(value);
                                if (!isNaN(numValue)) {
                                    handleAttributeChange(attribute.identifier, numValue);
                                } else {
                                    // Keep as string if conversion fails (will be handled by backend validation)
                                    handleAttributeChange(attribute.identifier, value);
                                }
                            }}
                            ExtraInputProps={{
                                type: "number",
                            }}
                        />
                    </Stack>
                );

            case JsonAttributeType.BOOLEAN:
                return (
                    <Stack key={attribute.identifier} flexDirection="column" gap={theme.spacing(1)}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={currentValue || false}
                                    onChange={(event) => {
                                        const checked = event.target.checked;
                                        handleAttributeChange(attribute.identifier, checked);
                                    }}
                                />
                            }
                            label={<Typography variant="body-semibold">{attribute.label}</Typography>}
                        />
                    </Stack>
                );

            default:
                return null;
        }
    };

    return (
        <Box>
            <Typography variant="h4" sx={{ marginBottom: theme.spacing(3) }}>
                {t("product.jsonAttributes.title", "Additional Information")}
            </Typography>
            <Stack spacing={3}>{productAttributes.map((attribute) => renderAttributeField(attribute))}</Stack>
        </Box>
    );
};
